import { LanguagesEnum, languageDisplayLabel } from '@/api/enums';
import { AppSpinner } from '@/ui/app-spinner';
import { AppChip } from '@/ui/chip';
import { AppProgressBar } from '@/ui/progressbar';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const TranslationProgressSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--default-padding);
  width: 100%;
  padding-block: var(--medium-big-padding);
`;

const SpinnerWrapper = styled.div`
  display: flex;

  .p-progress-spinner-circle {
    stroke: var(--red-main) !important;
  }
`;

const ProgressTitle = styled.h3`
  margin: 0;
  font-family: var(--font-family);
  font-size: var(--heading-medium-font-size);
  line-height: var(--default-line-height);
  font-weight: 700;
  color: var(--black-main);
  text-align: center;
`;

const ProgressSubtitle = styled.p`
  margin: 0;
  max-width: 542px;
  font-family: var(--font-family);
  font-size: var(--medium-font-size);
  line-height: var(--default-line-height);
  color: var(--black-main);
  text-align: center;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
  width: 100%;
  max-width: 542px;
`;

const ProgressBarWrapper = styled.div`
  flex: 1;
`;

const ProgressPercentage = styled.span`
  font-family: var(--font-family);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  font-weight: 600;
  color: var(--black-main);
`;

const LanguageList = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--xsmall-padding) var(--small-padding);
  width: 100%;
  max-width: 542px;
`;

const LanguageName = styled.span`
  font-family: var(--font-family);
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
  color: var(--black-main);
`;

const LanguagePill = styled(AppChip)`
  justify-self: end;

  &.chip-translation-done {
    color: var(--green-status);
    background: var(--green-pale);
  }

  &.chip-translation-pending {
    color: var(--yellow-dark);
    background: var(--yellow-pale);
  }
`;

export type PhishingTranslationLanguageStatus = 'done' | 'pending' | 'failed';

const pillTypeByStatus: Record<PhishingTranslationLanguageStatus, string> = {
  done: 'translation-done',
  pending: 'translation-pending',
  failed: 'error',
};

const pillLabelKeyByStatus: Record<PhishingTranslationLanguageStatus, string> =
  {
    done: 'phishing.translation.pill.done',
    pending: 'phishing.translation.pill.pending',
    failed: 'phishing.translation.pill.failed',
  };

export type PhishingTranslationProgressProps = {
  languages: LanguagesEnum[];
  completedLanguages: LanguagesEnum[];
  failedLanguages: LanguagesEnum[];
  progress: number;
};

export const PhishingTranslationProgress: React.FC<
  PhishingTranslationProgressProps
> = ({ languages, completedLanguages, failedLanguages, progress }) => {
  const { t } = useTranslation();

  const roundedProgress = Math.round(progress);

  const getLanguageStatus = (
    language: LanguagesEnum,
  ): PhishingTranslationLanguageStatus => {
    if (completedLanguages.includes(language)) return 'done';
    if (failedLanguages.includes(language)) return 'failed';

    return 'pending';
  };

  return (
    <TranslationProgressSection>
      <SpinnerWrapper>
        <AppSpinner width={136} height={136} strokeWidth={6} />
      </SpinnerWrapper>

      <ProgressTitle>{t('phishing.translation.progress.title')}</ProgressTitle>
      <ProgressSubtitle>
        {t('phishing.translation.progress.subtitle')}
      </ProgressSubtitle>

      <ProgressRow>
        <ProgressBarWrapper>
          <AppProgressBar value={roundedProgress} slim />
        </ProgressBarWrapper>
        <ProgressPercentage aria-live="polite">
          {t('phishing.translation.progress.percent', {
            progress: roundedProgress,
          })}
        </ProgressPercentage>
      </ProgressRow>

      <LanguageList>
        {languages.map((language) => {
          const status = getLanguageStatus(language);

          return (
            <React.Fragment key={language}>
              <LanguageName>{languageDisplayLabel[language]}</LanguageName>
              <LanguagePill
                size="s"
                type={pillTypeByStatus[status]}
                label={t(pillLabelKeyByStatus[status])}
                icon={status === 'done' ? 'pi pi-check' : undefined}
              />
            </React.Fragment>
          );
        })}
      </LanguageList>
    </TranslationProgressSection>
  );
};
